// src/components/PreviewModal.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./PreviewModalBookStyle.css";
import { createRazorpayOrder, verifyRazorpayPayment } from "../utils/payments";

const LIVE_KEY_ID = "rzp_live_R5DnQbQDXpHkMe";
const DISPLAY_LANGUAGE = "English";

// "₹29" → 29
function toRupees(v) {
  if (typeof v === "number") return v;
  const n = parseFloat(String(v ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

// --- Deterministic per-journal rating helpers ---
function hashString(str = "") {
  let h = 2166136261 >>> 0; // FNV-1a style
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seededRatingFor(journal) {
  const key = String(journal?.storagePath || journal?.title || "");
  const h = hashString(key);
  const choices = [4.7, 4.8, 4.9, 5.0];
  const picked = choices[h % choices.length];
  return picked === 5 ? "5" : picked.toFixed(1);
}

export default function PreviewModal({ journal, onClose }) {
  // ---- Hooks (must always run in the same order) ----
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  // rupees can be NaN when journal is missing; that's okay.
  const rupees = toRupees(journal?.price);

  // Prefer explicit journal.rating; otherwise use seeded rating
  const displayRating = useMemo(() => {
    if (!journal) return "5";
    if (journal.rating !== undefined && journal.rating !== null) {
      const r = Number(journal.rating);
      if (Number.isFinite(r)) return r === 5 ? "5" : r.toFixed(1);
      return String(journal.rating);
    }
    return seededRatingFor(journal);
  }, [journal]);

  // Pre-create an order when the modal opens (guarded inside the effect)
  useEffect(() => {
    let cancelled = false;

    async function prefetch() {
      try {
        if (!journal || !rupees || rupees <= 0) return;

        const ord = await createRazorpayOrder(rupees, {
          journalTitle: journal.title,
          language: DISPLAY_LANGUAGE, // ✅ English only
          storagePath: journal.storagePath || "",
          downloadUrl: journal.downloadUrl || "",
        });

        if (!cancelled) setOrder(ord || null);
      } catch {
        // silent fail; we'll create on click if prefetch fails
      }
    }

    prefetch();
    return () => {
      cancelled = true;
      setOrder(null);
    };
  }, [journal, rupees]);

  // Ensure order exists (used by hover/touch + click) — guarded inside.
  const ensureOrder = useCallback(async () => {
    if (order?.id) return order;
    if (!journal || !rupees || rupees <= 0) {
      throw new Error("Invalid amount or journal.");
    }
    const ord = await createRazorpayOrder(rupees, {
      journalTitle: journal.title,
      language: DISPLAY_LANGUAGE, // ✅ English only
      storagePath: journal.storagePath || "",
      downloadUrl: journal.downloadUrl || "",
    });
    setOrder(ord || null);
    return ord;
  }, [order, rupees, journal]);

  // ---- Handlers ----
  async function handleBuyNow() {
    if (!window.Razorpay) {
      alert("Payment module not loaded. Please refresh and try again.");
      return;
    }
    if (!journal) return;

    try {
      setLoading(true);

      const ord = await ensureOrder();
      if (!ord?.id || !ord?.amount) {
        throw new Error("Failed to create order. Please try again.");
      }

      const options = {
        key: LIVE_KEY_ID,
        amount: ord.amount,
        currency: ord.currency || "INR",
        name: "WhisperWall",
        description: journal.title,
        order_id: ord.id,
        prefill: { name: "", email: "", contact: "" },
        notes: {
          journalTitle: journal.title,
          language: DISPLAY_LANGUAGE, // ✅ English only
          storagePath: journal.storagePath || "",
        },
        theme: { color: "#7C3AED" },
        handler: async function (resp) {
          try {
            const result = await verifyRazorpayPayment({
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });

            if (result?.verified) {
              window.location.href =
                "/thank-you?pid=" + encodeURIComponent(resp.razorpay_payment_id);
              return;
            }

            alert(
              "Payment received but verification failed. Please contact support.\nPayment ID: " +
                resp.razorpay_payment_id
            );
          } catch (e) {
            console.error(e);
            alert(e.message || "Verification failed. Please contact support.");
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      alert(err.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ---- Render ----
  if (!journal) {
    return null;
  }

  return (
    <div className="ww-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="ww-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ww-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="ww-close" onClick={onClose} aria-label="Close preview">
          ×
        </button>

        <div className="ww-left">
          <img className="ww-cover" src={journal.image} alt={journal.title || "Journal"} />
          <div className="ww-rating">⭐ {displayRating} / 5</div>
        </div>

        <div className="ww-right">
          <h2 id="ww-title" className="ww-title">📖 {journal.title}</h2>
          <span className="ww-badge">Mini-Book Journal</span>
          <p className="ww-quote">“Your mind deserves peace and healing.”</p>
          {journal.summary ? <p className="ww-summary">{journal.summary}</p> : null}
          <div className="ww-price">
            {journal.price} — {DISPLAY_LANGUAGE}
          </div>
          <div className="ww-trust">🔒 Secure Checkout | Trusted by 10,000+</div>

        <button
            type="button"
            className="ww-buy"
            onClick={handleBuyNow}
            onMouseEnter={ensureOrder}
            onTouchStart={ensureOrder}
            disabled={loading}
            aria-busy={loading ? "true" : "false"}
          >
            {loading ? "Processing..." : "Buy Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
